import { ValidationMessages } from '@/utils/validation'

/**
 * API Error Codes
 * Based on the API documentation
 */
export enum ErrorCodes {
  // General errors
  UNKNOWN = -1,
  SUCCESS = 0,
  INVALID_COIN = 1,
  INVALID_POOL_CHART_PERIOD = 2,
  INVALID_ACCOUNT_CHART_PERIOD = 3,
  INVALID_PAGE_SIZE = 4,
  INVALID_PAGE_NUM = 5,
  INVALID_START_DATE = 6,
  INVALID_END_DATE = 7,
  INVALID_CHART_TYPE = 8,
  INVALID_SORT_FIELD = 9,
  INVALID_SORT_ORDER = 10,
  INTERNAL_ERROR = 15000,

  // JSON errors
  WRONG_JSON_BODY = 200,
  JSON_SYNTAX_ERROR = 201,
  MISSING_CONTENT_TYPE = 202,
  FAILED_EXTRACT_BODY = 203,
  WRONG_JSON = 204,

  // Authentication errors
  AUTH_REQUIRED = 2000,
  CAPTCHA_INVALID = 2002,
  CAPTCHA_REQUIRED = 2003,
  ACCESS_DENIED = 3000,
  INVALID_CREDENTIALS = 10000,

  // User errors
  USER_NOT_EXISTS = 5000,
  MINER_NOT_EXISTS = 5001,
  USER_USERNAME_EXISTS = 5002, // User with same username exists
  USER_EMAIL_EXISTS = 5003, // User with same email exists
  EMAIL_CONFIRMATION_NOT_EXISTS = 5004,
  EMAIL_CONFIRMATION_EXPIRED = 5005,
  EMAIL_CONFIRMATION_INVALID = 5006,
  EMAIL_CONFIRMATION_RATE_LIMIT = 5007,
  PASSWORD_RESET_TOKEN_EXPIRED = 5010,
  PASSWORD_RESET_TOKEN_INVALID = 5011,
  CHANGE_WALLET_TOKEN_EXPIRED = 5020,
  CHANGE_WALLET_TOKEN_INVALID = 5021,

  // 2FA errors
  TOTP_SECRET_GENERATE_ERROR = 7000,
  TOTP_CREATE_ERROR = 7001,
  TOTP_CREATE_QR_ERROR = 7002,
  INVALID_TOTP_TOKEN = 7003,
  MISSING_TOKEN_2FA = 7004,
  TWO_FA_DISABLED = 7005,
  TWO_FA_ALREADY_ENABLED = 7006,
  INVALID_TOTP_LENGTH = 7007,
  TOTP_NOT_GENERATED = 7008,

  // API errors
  INVALID_API_KEY = 12001,
  SIGNATURE_ERROR = 12002,

  // Validation errors
  INVALID_EMAIL_LENGTH = 11000,
  INVALID_USERNAME_LENGTH = 11001,
  INVALID_PASSWORD_LENGTH = 11002,
  INVALID_PASSWORD_TOKEN_LENGTH = 11003,
  INVALID_WALLET_TOKEN_LENGTH = 11004,
  INVALID_PASSWORD_CURRENT = 10001, // Invalid current password
  INVALID_PASSWORD = 13000,
  INVALID_USERNAME = 13001,
  INVALID_PAYMENT_THRESHOLD = 13002,
  INVALID_WALLET_ADDRESS = 13003,
  INVALID_USER_ID = 13004,

  // Internal errors
  DECODE_ERROR = 15001,
  SQL_ERROR = 15002
}

/**
 * Strongly typed error message interface
 */
export interface ErrorMessage {
  code: number
  message: string
}

/**
 * Centralized API error handler
 * Handles error code extraction, user-friendly messages, and routing
 */
export class ApiErrorHandler {
  private error: unknown

  constructor(error: unknown) {
    this.error = error
  }

  /**
   * Extracts error code from various error structures
   */
  getErrorCode(): ErrorCodes {
    if (!this.error) {
      return ErrorCodes.UNKNOWN
    }

    if (typeof this.error === 'object' && this.error !== null) {
      const errorObj = this.error as any

      // Direct access to the code (new ResponseHandler format)
      if ('code' in errorObj && typeof errorObj.code === 'number') {
        return errorObj.code as ErrorCodes
      }

      // Check nested response objects (Axios)
      if ('response' in errorObj && errorObj.response) {
        const response = errorObj.response as any

        if (response.data && typeof response.data === 'object' && 'code' in response.data) {
          return response.data.code as ErrorCodes
        }
        
        // HTTP status code fallback
        if (response.status) {
          return this.mapHttpStatusToErrorCode(response.status)
        }
      }

      // Check originalResponse from new ResponseHandler format
      if ('originalResponse' in errorObj && errorObj.originalResponse) {
        const response = errorObj.originalResponse as any

        if (response.data && typeof response.data === 'object' && 'code' in response.data) {
          return response.data.code as ErrorCodes
        }
        
        // HTTP status code fallback
        if (response.status) {
          return this.mapHttpStatusToErrorCode(response.status)
        }
      }
      
      // Check for HTTP status directly
      if ('status' in errorObj && typeof errorObj.status === 'number') {
        return this.mapHttpStatusToErrorCode(errorObj.status)
      }
    }

    return ErrorCodes.UNKNOWN
  }

  /**
   * Maps HTTP status codes to our ErrorCodes
   */
  private mapHttpStatusToErrorCode(status: number): ErrorCodes {
    switch (status) {
      case 400:
        return ErrorCodes.WRONG_JSON_BODY
      case 401:
        return ErrorCodes.AUTH_REQUIRED
      case 404:
        return ErrorCodes.USER_NOT_EXISTS
      case 429:
        return ErrorCodes.INTERNAL_ERROR
      case 500:
      case 502:
      case 503:
      case 504:
        return ErrorCodes.INTERNAL_ERROR
      default:
        return ErrorCodes.UNKNOWN
    }
  }

  /**
   * Extracts error message from the error object
   */
  getErrorMessage(): string {
    if (!this.error) {
      return ValidationMessages.ERROR_UNKNOWN
    }

    if (typeof this.error === 'object' && this.error !== null) {
      const errorObj = this.error as any

      // Direct access to the message (new ResponseHandler format)
      if ('message' in errorObj && typeof errorObj.message === 'string') {
        return errorObj.message
      }

      // Check nested response objects (Axios)
      if ('response' in errorObj && errorObj.response) {
        const response = errorObj.response as any

        if (response.data) {
          if (typeof response.data === 'string') {
            return response.data
          }
          
          if (typeof response.data === 'object' && 'message' in response.data) {
            return response.data.message
          }
          
          if (typeof response.data === 'object' && 'error' in response.data) {
            return response.data.error
          }
        }
        
        // HTTP status text fallback
        if (response.statusText) {
          return response.statusText
        }
      }

      // Check originalResponse from new ResponseHandler format
      if ('originalResponse' in errorObj && errorObj.originalResponse) {
        const response = errorObj.originalResponse as any

        if (response.data) {
          if (typeof response.data === 'string') {
            return response.data
          }
          
          if (typeof response.data === 'object' && 'message' in response.data) {
            return response.data.message
          }
          
          if (typeof response.data === 'object' && 'error' in response.data) {
            return response.data.error
          }
        }
      }
    }

    if (this.error instanceof Error) {
      return this.error.message
    }

    return ValidationMessages.ERROR_OCCURRED
  }

  /**
   * Checks if error is authentication related
   */
  isAuthError(): boolean {
    const code = this.getErrorCode()
    return code === ErrorCodes.AUTH_REQUIRED || code === ErrorCodes.INVALID_CREDENTIALS
  }

  /**
   * Gets user-friendly error message based on error code
   */
  getUserFriendlyMessage(): string {
    const code = this.getErrorCode()
    const originalMessage = this.getErrorMessage()

    // Map specific error codes to user-friendly messages (priority over text-based detection)
    const friendlyMessages: Record<ErrorCodes, string> = {
      [ErrorCodes.AUTH_REQUIRED]: ValidationMessages.AUTH_LOGIN_REQUIRED,
      [ErrorCodes.CAPTCHA_INVALID]: ValidationMessages.CAPTCHA_INVALID,
      [ErrorCodes.CAPTCHA_REQUIRED]: ValidationMessages.CAPTCHA_REQUIRED,
      [ErrorCodes.INVALID_CREDENTIALS]: ValidationMessages.AUTH_INVALID_CREDENTIALS,
      [ErrorCodes.USER_NOT_EXISTS]: ValidationMessages.USER_NOT_FOUND,
      [ErrorCodes.USER_USERNAME_EXISTS]: ValidationMessages.USERNAME_EXISTS,
      [ErrorCodes.USER_EMAIL_EXISTS]: ValidationMessages.USER_EMAIL_EXISTS,
      [ErrorCodes.EMAIL_CONFIRMATION_INVALID]: ValidationMessages.CONFIRMATION_CODE_INVALID,
      [ErrorCodes.EMAIL_CONFIRMATION_EXPIRED]: ValidationMessages.CONFIRMATION_CODE_EXPIRED,
      [ErrorCodes.EMAIL_CONFIRMATION_NOT_EXISTS]: ValidationMessages.CONFIRMATION_CODE_INVALID,
      [ErrorCodes.EMAIL_CONFIRMATION_RATE_LIMIT]: ValidationMessages.ERROR_EMAIL_CONFIRMATION_RATE_LIMIT,
      [ErrorCodes.PASSWORD_RESET_TOKEN_EXPIRED]: ValidationMessages.TOKEN_RESET_EXPIRED,
      [ErrorCodes.PASSWORD_RESET_TOKEN_INVALID]: ValidationMessages.TOKEN_RESET_INVALID,
      [ErrorCodes.INVALID_TOTP_TOKEN]: ValidationMessages.TFA_INVALID,
      [ErrorCodes.MISSING_TOKEN_2FA]: ValidationMessages.TFA_MISSING,
      [ErrorCodes.TWO_FA_ALREADY_ENABLED]: ValidationMessages.TFA_ALREADY_ENABLED,
      [ErrorCodes.TWO_FA_DISABLED]: ValidationMessages.TFA_NOT_ENABLED,
      [ErrorCodes.INVALID_WALLET_ADDRESS]: ValidationMessages.WALLET_INVALID,
      [ErrorCodes.INVALID_PAYMENT_THRESHOLD]: ValidationMessages.AMOUNT_RANGE,
      [ErrorCodes.INVALID_PASSWORD_CURRENT]: ValidationMessages.PASSWORD_CURRENT_INCORRECT,
      [ErrorCodes.INVALID_PASSWORD]: ValidationMessages.PASSWORD_STRONG,
      [ErrorCodes.INVALID_PASSWORD_LENGTH]: ValidationMessages.PASSWORD_LENGTH_RANGE,
      [ErrorCodes.ACCESS_DENIED]: ValidationMessages.ERROR_ACCESS_DENIED,
      [ErrorCodes.INTERNAL_ERROR]: ValidationMessages.ERROR_SERVER,
      [ErrorCodes.UNKNOWN]: ValidationMessages.ERROR_OCCURRED
    } as Record<ErrorCodes, string>

    // If we have a specific error code mapping, use it
    if (friendlyMessages[code]) {
      return friendlyMessages[code]
    }

    // Handle text-based error messages for cases without specific error codes
    if (originalMessage && typeof originalMessage === 'string') {
      const lowerMessage = originalMessage.toLowerCase()
      
      // Handle payment threshold validation errors
      if (
        lowerMessage.includes('payment_threshold') ||
        (lowerMessage.includes('min') && lowerMessage.includes('max') && lowerMessage.includes('0.'))
      ) {
        return ValidationMessages.AMOUNT_RANGE
      }

      // Handle 2FA related errors
      if (lowerMessage.includes('2fa') || lowerMessage.includes('token_2fa') || lowerMessage.includes('totp')) {
        return ValidationMessages.TFA_AUTHENTICATION_INVALID
      }

      // Handle wallet address errors
      if (lowerMessage.includes('wallet') && lowerMessage.includes('address')) {
        return ValidationMessages.WALLET_INVALID
      }
      
      // Handle username errors
      if (lowerMessage.includes('username') && lowerMessage.includes('exists')) {
        return ValidationMessages.USERNAME_EXISTS
      }
      
      // Handle email errors (but not confirmation code errors)
      if (lowerMessage.includes('email') && lowerMessage.includes('exists') && !lowerMessage.includes('confirmation')) {
        return ValidationMessages.USER_EMAIL_EXISTS
      }
    }

    return originalMessage || ValidationMessages.ERROR_OCCURRED
  }

  /**
   * Handles the error with optional authentication redirect
   */
  handle(redirectOnAuthError: boolean = true): ErrorCodes {
    const code = this.getErrorCode()

    // Redirect to login if authentication error and redirect is enabled
    if (this.isAuthError() && redirectOnAuthError) {
      try {
        // Dynamic import to avoid circular dependencies
        import('@/router').then((routerModule) => {
          const router = routerModule.default
          router.push({ name: 'login' })
        }).catch((error) => {
          console.error('Error importing router for redirect:', error)
        })
      } catch (error) {
        console.error('Error redirecting to login:', error)
      }
    }

    return code
  }


}
